import * as zod from 'zod';

/**
 * What the on-box `restore` service tells the UI about a cloud-ordered
 * checkpoint restore, and what the UI sends back.
 *
 * A restore is destructive — pg_restore into a fresh cluster plus a wholesale
 * wipe of the backed-up file areas — so it does not begin on delivery. This
 * is the contract the confirmation modal is built on: what is about to be
 * restored, how it differs from this box, and the operator's answer.
 */

export const RestoreState = zod.enum([
  /** No restore has been delivered, or the last one has been dismissed. */
  'idle',
  /** Delivered and waiting for someone at the box to answer. */
  'awaitingConfirmation',
  /** Answered; updater-bot has not started the engine yet (one phone-home
   *  cycle at most). */
  'confirmed',
  /** Refused at the robot. Terminal for this command — the cloud ledger
   *  records the refusal, and running it takes a newly ordered one. */
  'declined',
  /** The engine is applying. Do not power the robot off. */
  'running',
  'succeeded',
  'failed',
]);

export type RestoreState = zod.infer<typeof RestoreState>;

export const RestoreTrainingRun = zod.object({
  trainingRunId: zod.string(),
  epoch: zod.number().int().nullable(),
  downloadPath: zod.string(),
});

export type RestoreTrainingRun = zod.infer<typeof RestoreTrainingRun>;

/** The identity of the box the checkpoint was captured on, as recorded in
 *  its manifest. Every field is optional: capture is best-effort and an
 *  older manifest may simply not carry one. */
export const RestoreCheckpointSource = zod.object({
  buildId: zod.string().optional(),
  buildName: zod.string().optional(),
  armModel: zod.string().optional(),
  armSerial: zod.string().optional(),
  operationMode: zod.string().optional(),
  hostType: zod.string().optional(),
});

export type RestoreCheckpointSource = zod.infer<typeof RestoreCheckpointSource>;

export const RestoreCheckpointSummary = zod.object({
  robotId: zod.string(),
  backupKey: zod.string(),
  label: zod.string().optional(),
  createdAt: zod.string().optional(),
  trigger: zod.string().optional(),
  source: RestoreCheckpointSource,
  /** Models the source box had. These do not travel inside the checkpoint —
   *  the box re-requests the ones the operator picks. */
  trainingRuns: zod.array(RestoreTrainingRun),
});

export type RestoreCheckpointSummary = zod.infer<
  typeof RestoreCheckpointSummary
>;

/** The same facts read off THIS box, for the modal to contrast. */
export const RestoreLocalFacts = zod.object({
  buildId: zod.string().optional(),
  buildName: zod.string().optional(),
  armModel: zod.string().optional(),
  armSerial: zod.string().optional(),
  operationMode: zod.string().optional(),
  /** False when this box's operation mode means updater-bot never downloads
   *  trained models, whatever is queued. */
  modelDownloadsEnabled: zod.boolean(),
});

export type RestoreLocalFacts = zod.infer<typeof RestoreLocalFacts>;

export const RestoreDifferenceKind = zod.enum([
  'build',
  'operationMode',
  'armModel',
  'armSerial',
]);

export type RestoreDifferenceKind = zod.infer<typeof RestoreDifferenceKind>;

export const RestoreDifference = zod.object({
  kind: RestoreDifferenceKind,
  /** Human label for the row, e.g. "Arm serial". */
  label: zod.string(),
  checkpointValue: zod.string(),
  localValue: zod.string(),
  /**
   * 'blocking' differences abort the restore at the box (the engine
   * re-checks arm model before it wipes anything). Everything else is a
   * warning: the cloud gate let it through, and the operator is the last
   * person who can say it is wrong.
   */
  severity: zod.enum(['warning', 'blocking']),
});

export type RestoreDifference = zod.infer<typeof RestoreDifference>;

/**
 * What became of the checkpoint's models on the restore target. Produced by
 * api-srv when the box re-requests them and mirrored here so the shortfall is
 * visible on the box — an operator who selected five models and got three
 * should not have to read cloud logs to find that out.
 */
export const RestoreModelReportZod = zod.object({
  /** Runs the operator selected and the box asked for. */
  requested: zod.number().int().nonnegative(),
  /** Rows actually inserted on the download queue. */
  enqueued: zod.number().int().nonnegative(),
  /** Runs the target already has, or is already fetching. */
  alreadyPresent: zod.number().int().nonnegative(),
  /** Runs the checkpoint names that no longer exist in the cloud — deleted
   *  since the backup was taken, and unrestorable. */
  unknownTrainingRunIds: zod.array(zod.string()),
  /** Runs that exist but whose queue row could not be written. */
  failedTrainingRunIds: zod.array(zod.string()),
  /** Runs whose download later failed on the box. That failure is terminal
   *  (finalizeTrainingRunDownload writes `finished`), so it is folded back
   *  in here rather than left to a retry that never comes. */
  failedDownloadTrainingRunIds: zod.array(zod.string()).default([]),
  /** Set when the re-request could not be made at all. */
  error: zod.string().optional(),
  /** False when this box's operation mode means updater-bot will not download
   *  models at all, however many are queued (see checkAndDownloadTrainedModels). */
  downloadsEnabledOnBox: zod.boolean(),
});

export type RestoreModelReport = zod.infer<typeof RestoreModelReportZod>;

/**
 * Whether the robot is doing something a restore must not interrupt.
 *
 * A restore stops every service and replaces the database, so confirming
 * one mid-routine would tear the stack down under a running program. The
 * modal reports this, the confirm is refused while it holds, and — because
 * the modal covers the whole screen, including the on-screen stop controls —
 * it also stops being blocking so the operator can go and stop the robot.
 */
export const RestoreRobotActivity = zod.object({
  isBusy: zod.boolean(),
  /** Operator-readable reason, present only while busy. */
  reason: zod.string().optional(),
});

export type RestoreRobotActivity = zod.infer<typeof RestoreRobotActivity>;

export const RestoreData = zod.object({
  state: RestoreState,
  commandId: zod.string().optional(),
  backupKey: zod.string().optional(),
  deliveredAt: zod.string().optional(),
  startedAt: zod.string().optional(),
  finishedAt: zod.string().optional(),
  /** Failure reason, or — on a succeeded restore — the note about what did
   *  not come over with it. */
  error: zod.string().optional(),
  checkpoint: RestoreCheckpointSummary.optional(),
  local: RestoreLocalFacts,
  /** What the robot is doing right now, which decides whether the restore
   *  may be confirmed at all. */
  robotActivity: RestoreRobotActivity,
  differences: zod.array(RestoreDifference),
  /** Present once the operator has answered. */
  selectedTrainingRunIds: zod.array(zod.string()).optional(),
  modelReport: RestoreModelReportZod.optional(),
});

export type RestoreData = zod.infer<typeof RestoreData>;

export const RestoreConfirmationInput = zod.object({
  /** Must match the delivered command, so a stale browser tab cannot
   *  confirm a restore the operator never saw. */
  commandId: zod.string(),
  decision: zod.enum([
    /** Run it. */
    'confirm',
    /** Refuse it here; the cloud ledger records a failure with the reason. */
    'decline',
    /** Dismiss a finished restore from the screen. */
    'acknowledge',
  ]),
  /** Which of the checkpoint's models to re-request. Only read on 'confirm';
   *  omitted means none. */
  trainingRunIds: zod.array(zod.string()).optional(),
});

export type RestoreConfirmationInput = zod.infer<
  typeof RestoreConfirmationInput
>;
